/**
 * ForecastChart の Y 軸 (需要 = 第 1 軸 / 温度 = 第 2 軸) の domain・ticks を
 * 算出する純関数群。
 *
 * design 参照: design/demand/forecast-today.html L4296-4398。
 */
import { chartSeriesToggleKey } from '~/pages/DemandForecastDaily/models/chartLegend'

/** Recharts YAxis の domain に渡せる形 (数値 domain または 'auto' / 'dataMin - N' 等の指定子)。 */
export type ChartAxisDomain = [number | string, number | string]

export type ChartAxisConfig = {
  demandDomain: ChartAxisDomain
  demandTicks: number[] | undefined
  temperatureDomain: ChartAxisDomain
  temperatureTicks: number[] | undefined
}

export type AxisSeriesKeys = {
  /** 需要 (第 1 軸) に載る系列の dataKey 一覧。 */
  demandKeys: string[]
  /** 温度 (第 2 軸) に載る系列のうち表示中のものの dataKey 一覧。 */
  temperatureKeys: string[]
}

export type ResolveAxisSeriesKeysParams = {
  /**
   * `buildChartData` の出力行。行に実在する key = 実際に描画される系列なので、
   * 系列の有無 (どの断面でどの系列が出るか) はここから読み取る。
   */
  data: ReadonlyArray<Record<string, number | null | string>>
  /**
   * 右軸 (温度軸) 自体を描画するか (= `hasChartTemperatureData`)。
   * 温度系列のスロットは `showTemperature` と無関係に `buildChartData` へ渡される
   * (= 全コマ欠測でも key は行に現れる) ため、右軸の描画可否だけは行から導けず
   * 明示的に受け取る。
   */
  showTemperature: boolean
  /** 凡例 (ForecastChartLegend) で OFF にされた系列 key の集合。 */
  hiddenKeys: ReadonlySet<string>
}

/** 温度 (第 2 軸) に載る系列の dataKey。 */
const TEMPERATURE_SERIES_KEYS: ReadonlySet<string> = new Set([
  'tempForecast',
  'tempActual',
  'dewActual',
])

/** 過去実績日ごとの実績気温系列 (温度軸) の dataKey prefix (`pastTemp_0` 等)。 */
const PAST_TEMPERATURE_KEY_PREFIX = 'pastTemp_'

/** 過去実績日ごとの実績露点系列 (温度軸) の dataKey prefix (`pastDew_0` 等)。 */
const PAST_DEWPOINT_KEY_PREFIX = 'pastDew_'

function isTemperatureKey(key: string): boolean {
  return (
    TEMPERATURE_SERIES_KEYS.has(key) ||
    key.startsWith(PAST_TEMPERATURE_KEY_PREFIX) ||
    key.startsWith(PAST_DEWPOINT_KEY_PREFIX)
  )
}

/**
 * 凡例「露点」トグル ('dewpoint') に相乗りする dataKey 判定。対象日実績露点
 * (予測露点は spec #4290 改訂 2026-08-14 で撤去済み) と過去日露点
 * (`pastDew_*`) を単一トグル key に写像する `chartSeriesToggleKey`
 * (`models/chartLegend.ts`) と対応する。ここで写像を再実装せず判定だけを閉じるのは、
 * chartAxis は domain 入力の可視判定のみを行い、トグル key 集合の生成 (凡例側の
 * 責務) を持たないため。
 */
function isDewpointKey(key: string): boolean {
  return key === 'dewActual' || key.startsWith(PAST_DEWPOINT_KEY_PREFIX)
}

/**
 * チャートデータ行に実在する系列 key を出現順に集める。X 軸ラベル (値が文字列の
 * key = `koma`) は系列ではないため除く。
 */
function collectSeriesKeys(
  data: ReadonlyArray<Record<string, number | null | string>>,
): string[] {
  const seriesKeys: string[] = []
  const seen = new Set<string>()
  const labelKeys = new Set<string>()
  for (const row of data) {
    for (const [key, value] of Object.entries(row)) {
      if (typeof value === 'string') labelKeys.add(key)
      else if (!seen.has(key)) {
        seen.add(key)
        seriesKeys.push(key)
      }
    }
  }
  return seriesKeys.filter((key) => !labelKeys.has(key))
}

/**
 * 軸ごとの domain 入力系列を、チャートデータ行に実在する key から解決する。
 *
 * 「どの系列が存在するか」の判定を引数で受け取らないのは、系列の描画条件が
 * `buildChartData` の出力 key と同義だから (= その系列の key が行に無ければ、
 * その系列は描画されず domain にも寄与しない)。判定を複製せず行から読むことで、
 * 描画条件と domain 入力のズレが構造的に起こらない。
 */
export function resolveAxisSeriesKeys({
  data,
  showTemperature,
  hiddenKeys,
}: ResolveAxisSeriesKeysParams): AxisSeriesKeys {
  const seriesKeys = collectSeriesKeys(data)
  const visible = (key: string): boolean => !hiddenKeys.has(key)

  return {
    // 需要軸は**全系列**を返す。 表示中だけに絞るのは呼び出し元 (ForecastChart) の
    // 責務で、 そこで「表示中が 1 つも無い」 (= 軸自体を出さない) と
    // 「表示中で domain を決める」 を出し分けるため、 ここで絞ると
    // その 2 状態が区別できなくなる。
    //
    // 実際に描かれる domain は **表示中の需要系列のみ**から決まる (issue #6714、
    // ForecastChart の `computeChartAxisConfig` 呼び出し参照)。 以前は表示状態に
    // 依らず全系列で domain を決めていたが、 需要想定値を消しても軸の最大値が
    // 残って過去実績が潰れるため反転した。 温度軸 (下) の「表示中のみ」 (AC-04) と
    // 揃った扱いになる。
    demandKeys: seriesKeys.filter((key) => !isTemperatureKey(key)),
    // 露点は予測/実績/過去日 (`pastDew_*`) を 1 つの凡例 key ('dewpoint') で
    // まとめてトグルする (`chartSeriesToggleKey` と対応)。
    temperatureKeys: showTemperature
      ? seriesKeys.filter(
          (key) =>
            isTemperatureKey(key) &&
            (isDewpointKey(key) ? visible('dewpoint') : visible(key)),
        )
      : [],
  }
}

/**
 * チャートデータ行から指定 key の数値だけを集める (欠測 = null、X 軸ラベル = string は
 * 除く)。`buildChartData` の出力をそのまま domain 算出の入力にするための橋渡し。
 */
/**
 * 需要軸の domain に渡す値 (issue #6714 / #6814)。
 *
 * **表示中の需要系列だけ**を集める。 凡例で消した系列は軸レンジに影響しない
 * (温度軸の従来挙動と同じ)。
 *
 * **表示中が 0 本でも空配列を返す (全系列へのフォールバックを持たない)。**
 * ここで全系列に戻すと `computeChartAxisConfig` が同じ値を温度軸の帯分離
 * (`demandTroughRatio`) にも使うため、 見えていない需要カーブを避けようとして
 * **気温カーブが下端へ圧縮される** (devin review 指摘 #6814)。 需要が 1 本も
 * 見えない時は避ける相手が居ないのが正しい。 左軸自体の非表示は呼び出し元が
 * 「値が 0 件か」 で判断する。
 *
 * 左 YAxis の**幅**は別途 全系列から決める (WeatherBar と共有していて、
 * 凡例トグルで動くと気象帯の横位置整列がずれるため)。
 */
export function resolveDemandDomainValues(
  data: ReadonlyArray<Record<string, number | null | string>>,
  demandKeys: readonly string[],
  hiddenKeys: ReadonlySet<string>,
): number[] {
  return collectSeriesValues(
    data,
    demandKeys.filter((key) => !hiddenKeys.has(chartSeriesToggleKey(key))),
  )
}

export function collectSeriesValues(
  data: ReadonlyArray<Record<string, number | null | string>>,
  keys: readonly string[],
): number[] {
  const values: number[] = []
  for (const row of data) {
    for (const key of keys) {
      const value = row[key]
      if (typeof value === 'number') values.push(value)
    }
  }
  return values
}

/**
 * 需要 (左) 軸の目盛りラベル (千区切りの数値のみ、単位サフィックスなし。
 * design L3170-3172: 単位はカード見出し「単位：kWh」〈KomaCard〉が担う。
 * demand-fix-48-koma / AC-DFC-011 で design の「MWh」表記は kWh に統一済み)。
 *
 * 目盛りの実値をそのまま整形する (丸めない)。nice step は 2.5 × 10^n を返しうるため
 * 非整数の目盛りが立つことがあり、ラベル側で丸めると目盛り位置とラベルがずれる。
 * チャートドラッグ編集 (`models/chartDrag.ts` の `extractYAxisTickPoints`) は描画済み
 * ラベルの文字列を parseFloat して pixel↔値スケールを組むため、このズレは
 * ドラッグ確定値 (= 保存対象の需要想定値) の系統的な誤差に直結する。
 */
export function formatDemandTickLabel(value: number): string {
  return value.toLocaleString('ja-JP')
}

/** 需要軸の上部余白係数 (design L4322 `_demMax * 1.25`)。 */
const DEMAND_TOP_PADDING_RATIO = 1.25

/** 需要軸の目盛り本数目標 (design L4323 `_dftNiceStep(_rawTop, 5)`)。 */
const DEMAND_TICK_TARGET = 5

/**
 * 需要 (左) 軸目盛りラベルの推定幅 (px/文字)。
 *
 * `formatDemandTickLabel` で `ja-JP` ロケールの千区切りを掛けるため、桁数に応じた
 * 幅を文字数から概算する。実フォント (11px) の半角数字・カンマ幅を考慮して余裕を
 * 持たせた値 (fix #5432)。
 */
const DEMAND_TICK_LABEL_PX_PER_CHAR = 8

/**
 * 需要軸幅に加える余白 (px)。
 *
 * Recharts の左 YAxis 幅には tickLine 長さ・tick と label の間隔・label の内側余白が
 * 含まれる。実測から `width - 16` px 程度がラベル有効幅となる。
 * フォント・環境差で 1〜2px 程度の揺らぎがあるため、18px として安全マージンを
 * 持たせる (fix #5432)。
 */
const DEMAND_AXIS_WIDTH_PADDING = 18

/**
 * 端数の出ない目盛り幅 (1/2/2.5/5 × 10^n) を返す
 * (design L4309-4316 `_dftNiceStep`)。
 */
function niceStep(range: number, target: number): number {
  const rough = Math.max(1, range) / target
  const mag = 10 ** Math.floor(Math.log10(rough))
  const norm = rough / mag
  const nice =
    norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 2.5 ? 2.5 : norm <= 5 ? 5 : 10
  return nice * mag
}

/**
 * 目盛り値を step の桁で丸め、`i * step` の積で出る浮動小数の誤差
 * (例: step 0.2 の 3 本目が 0.6000000000000001) を軸ラベルから除く。
 * step が 1 以上 (整数刻み) の場合は丸め桁 0 = 値をそのまま返す。
 */
function roundToStepPrecision(value: number, step: number): number {
  const decimals = Math.max(0, -Math.floor(Math.log10(step)) + 1)
  const factor = 10 ** decimals
  return Math.round(value * factor) / factor
}

/** 温度軸の下側余白係数 (design L4387 `_tMin - _tRange * 0.15`)。 */
const TEMPERATURE_BOTTOM_PADDING_RATIO = 0.15

/** 温度軸の上側余白係数 (design L4387 `_tMax + _tRange * 5`)。温度帯を下部へ寄せる。 */
const TEMPERATURE_TOP_PADDING_RATIO = 5

/** 温度レンジの下限 (design L4384 `Math.max(4, _tMax - _tMin)`)。 */
const TEMPERATURE_MIN_RANGE = 4

/**
 * 温度軸の目盛り本数の上限。通常の気温・露点レンジ (step 5℃ / 10℃) では 10 本未満に
 * 収まるため描画には影響せず、異常値 (極端に大きい気温) が届いた時だけ効く安全弁。
 */
const MAX_TEMPERATURE_TICK_COUNT = 64

/**
 * 需要トラフが深く design の固定比率だけでは帯が重なる場合に、温度帯の上端を
 * 需要トラフのどこまで下げるか (= 安全マージン)。1 未満であれば AC-03 の
 * 「重ならない」が厳密に成立する。
 */
const TEMPERATURE_BAND_SAFETY_RATIO = 0.9

/**
 * 温度帯の高さ (= 帯比率) の可読性フロア F。トラフ調整による温度帯の圧縮をここで
 * 打ち切る。「design 比率の何分の 1」のような恣意的な定数ではなく、**温度軸の目盛り
 * ラベルが縦に衝突しない最小の帯高さ**から逆算している。導出 (すべてコード上の実定数):
 *
 * - 目盛りラベルのフォントサイズ = 12px
 *   (`components/ForecastChart.tsx` の右 YAxis `tick={{ fontSize: 12, ... }}`。
 *   2026-09-18 デザイナー指摘で 11px から変更)
 * - ラベル 1 行の占有高 = ceil(12 × 1.2) = 15px (行高 1.2em)。隣接ラベルの中心間
 *   距離がこれを下回ると文字が縦に重なる
 * - plot 高 ≒ 334px = チャート container 380px (`ForecastChart.tsx` の
 *   `ResponsiveContainer` を包む div の `height: 380`) − `CHART_MARGIN.top` 12 −
 *   `CHART_MARGIN.bottom` 4 − X 軸の高さ 30 (Recharts XAxis の既定 height)
 * - 温度軸に立つ目盛りの本数 = 4 本 (代表値)。`buildTemperatureTicks` はレンジ 20℃
 *   以下で 5℃ 刻みなので、日較差 15℃ 前後の典型日 (例 [10,25] → [10,15,20,25]) が
 *   4 本になる
 *
 * 4 本 = 3 間隔 → 必要な帯高さ = 15px × 3 = 45px → 45 ÷ 334 = 13.5% → 安全側へ
 * 切り上げて 14%。
 *
 * 目盛り本数は実データ範囲に依存する (日較差ちょうど 20℃ では 5 本立ちうる) が、
 * 描画実測を軸算出の純関数へ持ち込まないため動的計算にはせず、上記の代表値で置いた
 * 定数 1 個に留める。フロア無し (= トラフ比に比例して温度帯が潰れる) と比べれば
 * 可読性の改善は十分に大きい。
 */
const TEMPERATURE_BAND_MIN_RATIO = 0.14

/**
 * 需要 (左) / 温度 (右) 両軸の domain と目盛りを算出する (spec AC-005b)。
 *
 * 出力 domain で描画したとき、値が定義された需要データ点は上部帯・温度軸系列の
 * データ点は下部帯に収まり、両者の正規化位置 ((v − min) ÷ (max − min)) は縦方向に
 * 重ならない。需要軸は design 準拠で 0 起点を保つため、Area の塗り面 (y=0 まで) と
 * 需要値 0 のコマはこの保証の対象外。加えて需要トラフ比が概ね 15.6% を下回る断面も
 * 対象外で、温度軸の可読性を優先して重なりを許容する (`computeTemperatureAxis` の
 * 可読性フロア `TEMPERATURE_BAND_MIN_RATIO` 参照)。
 *
 * 入力はどちらも**表示中の系列の値**を渡す (issue #6714 で需要軸を温度軸に揃えた。
 * 以前は需要軸だけ凡例 OFF の系列も含めていた):
 * - `demandValues`: 需要軸に載る系列のうち表示中のものの値。 需要軸の domain に
 *   加えて、 **温度軸の帯分離 (`demandTroughRatio`) の基準**にもなる。 表示中の
 *   需要が 0 本なら空配列を渡すこと — 見えない需要カーブを避けようとして
 *   気温が下端へ圧縮されるため (devin review 指摘 #6814)
 * - `temperatureValues`: 温度軸に載る系列のうち表示中のものの値
 *
 * どちらの入力も系列名を列挙せず値の集合だけを受け取るため、将来の系列追加
 * (確定参照列・過去日露点等) は呼び出し側の key 解決を足すだけで自動的に算入される。
 *
 * 入力に非有限値 (Infinity / NaN) が混ざる場合はここで除外する。値の入口
 * (`models/slotHelpers.ts` の decimalToNumber) も Number.isFinite で欠測に畳むため
 * 実経路では二重防御だが、本関数は系列名を持たない値の集合だけを受け取る純関数
 * (= 入口を経由しない呼び出しもありうる) なので、非有限値に対する契約はここでも
 * 保つ。非有限値は domain を壊し、目盛り生成の反復回数を値の大きさに比例させる
 * (= 描画スレッドが止まる) ため、軸ごとの算出に入る前に落とす。有限値が 1 つも
 * 残らない場合は「値なし」と同じフォールバック経路に落ちる。
 */
export function computeChartAxisConfig({
  demandValues,
  temperatureValues,
}: {
  demandValues: readonly number[]
  temperatureValues: readonly number[]
}): ChartAxisConfig {
  const finiteDemandValues = demandValues.filter((v) => Number.isFinite(v))
  const finiteTemperatureValues = temperatureValues.filter((v) =>
    Number.isFinite(v),
  )

  const demand = computeDemandAxis(finiteDemandValues)
  const temperature = computeTemperatureAxis(
    finiteTemperatureValues,
    demandTroughRatio(finiteDemandValues, demand.top),
  )

  return {
    demandDomain: demand.domain,
    demandTicks: demand.ticks,
    temperatureDomain: temperature.domain,
    temperatureTicks: temperature.ticks,
  }
}

/**
 * 需要 (左) 軸の domain / ticks (design L4317-4328)。値が 1 つも無い場合 (翌日/
 * 翌々日断面の初期表示 = spec AC-003) は Recharts の自動 domain にフォールバック
 * する。
 */
function computeDemandAxis(values: readonly number[]): {
  domain: ChartAxisDomain
  ticks: number[] | undefined
  /** 0 起点 domain 上での帯分離判定に使う上端値 (自動 domain の場合は undefined)。 */
  top: number | undefined
} {
  if (values.length === 0) {
    return { domain: ['auto', 'auto'], ticks: undefined, top: undefined }
  }

  // design L4322-4328: 0 起点 + 上部約 25% の余白を nice step へ丸める。
  const rawTop = Math.max(...values) * DEMAND_TOP_PADDING_RATIO
  // 入口 (slotHelpers の decimalToNumber) は Number.isFinite 基準なので、1.7e308 の
  // ような**有限だが極大な値**はここまで通過する。その場合 rawTop が overflow して
  // Infinity になり、niceStep の norm = Infinity / Infinity = NaN → step = Infinity
  // → top = NaN → domain [0, NaN] になる。値なしフォールバックへ落とす。
  if (!Number.isFinite(rawTop)) {
    return { domain: ['auto', 'auto'], ticks: undefined, top: undefined }
  }
  const step = niceStep(rawTop, DEMAND_TICK_TARGET)
  const top = Math.max(step, Math.ceil(rawTop / step) * step)
  // rawTop が有限でも top (= rawTop を step の倍数へ切り上げた値) は overflow しうる
  // (例: 1.4e308 → rawTop 1.75e308 は有限だが top は Infinity)。top が非有限だと
  // 下の目盛り生成の反復回数 count も Infinity になり、ループが停止しない。
  if (!Number.isFinite(top)) {
    return { domain: ['auto', 'auto'], ticks: undefined, top: undefined }
  }
  // 目盛りは index ベースで生成する (加算の累積誤差を持ち越さず、絶対値の許容差に
  // 依存しないため domain 外の目盛りが出ない)。
  const count = Math.round(top / step)
  const ticks: number[] = []
  for (let i = 0; i <= count; i++) {
    ticks.push(roundToStepPrecision(i * step, step))
  }

  return { domain: [0, top], ticks, top }
}

/**
 * 需要 (左) 軸の tick 列から、最大桁数のラベルが切れずに入る幅 (px) を概算する。
 *
 * 目盛りが無い (値なし / 全系列非表示) 場合は `minWidth` を返す。
 * Recharts の `<YAxis width>` にそのまま渡すことを想定し、`tickLine` や内側余白を
 * 含めて `+ 18px` する (fix #5432)。
 */
export function computeDemandAxisWidth(
  ticks: number[] | undefined,
  minWidth: number,
): number {
  if (ticks === undefined || ticks.length === 0) {
    return minWidth
  }
  let maxChars = 0
  for (const tick of ticks) {
    maxChars = Math.max(maxChars, formatDemandTickLabel(tick).length)
  }
  const labelWidth = maxChars * DEMAND_TICK_LABEL_PX_PER_CHAR
  return Math.max(minWidth, labelWidth + DEMAND_AXIS_WIDTH_PADDING)
}

/**
 * 需要トラフ (= 値が定義され 0 でない最小の需要値) の 0 起点 domain 上の正規化位置。
 * 温度帯の上端をこの位置より下に抑えることで AC-03 の帯分離が成立する。
 * 需要値 0 のコマと Area の塗り面 (y=0 まで) は AC-03 の保証対象外のため、
 * トラフの算出からも 0 を除く。
 */
function demandTroughRatio(
  values: readonly number[],
  top: number | undefined,
): number | undefined {
  if (top === undefined || top <= 0) return undefined
  const positives = values.filter((v) => v > 0)
  if (positives.length === 0) return undefined
  return Math.min(...positives) / top
}

/**
 * 温度 (右) 軸の domain (design L4381-4393)。domain を上方向へ大きく広げることで
 * 温度軸系列を描画域の下部帯へ寄せ、需要系列 (第 1 軸) と縦方向に重ならせない
 * (spec AC-005b / AC-03)。
 *
 * `troughRatio` (需要トラフの正規化位置) が渡された場合は、design 既定の上側余白
 * (5x) では帯が重なるケース (= 需要トラフが深い) に限り上側余白を追加で広げて
 * 温度帯をさらに下へ寄せる。需要軸の 0 起点は design L4320 のユーザー指示のため
 * 動かさず、調整は温度軸の帯比率側だけで行う。
 *
 * ただしこの拡張は可読性フロア `TEMPERATURE_BAND_MIN_RATIO` (= F = 14%。温度軸の
 * 目盛りラベル 11px・plot 高 334px・目盛り 4 本から逆算した「ラベルが縦に衝突しない
 * 最小の帯高さ」。導出は同定数の JSDoc) で打ち切る。したがって:
 *
 * - 需要トラフ比が F ÷ 安全マージン (= 0.14 ÷ 0.9 ≒ 15.6%) を下回る断面では、
 *   spec AC-005b の「重ならない」は**成立しない**。分離しきるには温度帯を目盛り
 *   ラベルが読めない高さまで潰す必要があるため、温度軸の可読性を優先する
 *   (ユーザー確定の設計判断)。
 * - よって **AC-03 (帯分離) の保証範囲は「需要トラフ比が概ね 15.6% 以上」に限定される**。
 *   それを下回る断面では温度カーブが需要カーブと縦方向に重なって描かれる。
 */
function computeTemperatureAxis(
  values: readonly number[],
  troughRatio: number | undefined,
): {
  domain: ChartAxisDomain
  ticks: number[] | undefined
} {
  if (values.length === 0) {
    // 温度データはあるが全温度系列が凡例 OFF のケース (右軸の描画判定は hiddenKeys を
    // 見ないため軸自体は残る)。従来どおりの自動 domain に委ね NaN domain を作らない。
    return { domain: ['dataMin - 2', 'dataMax + 3'], ticks: undefined }
  }

  const min = Math.min(...values)
  const max = Math.max(...values)
  const range = Math.max(TEMPERATURE_MIN_RANGE, max - min)
  const lo = Math.floor(min - range * TEMPERATURE_BOTTOM_PADDING_RATIO)
  const designHi = Math.ceil(max + range * TEMPERATURE_TOP_PADDING_RATIO)
  const hi =
    troughRatio !== undefined
      ? Math.max(
          designHi,
          // 温度帯上端の正規化位置 (max − lo) ÷ (hi − lo) を
          // troughRatio × 安全マージン 以下に収める hi を解く。ただし帯比率が
          // 可読性フロア F を下回る hi は採用せず、拡張を F 相当で打ち切る
          // (= 目盛りラベルが縦に衝突する高さまで温度帯を潰さない)。
          Math.ceil(
            lo +
              (max - lo) /
                Math.max(
                  troughRatio * TEMPERATURE_BAND_SAFETY_RATIO,
                  TEMPERATURE_BAND_MIN_RATIO,
                ),
          ),
        )
      : designHi

  // 需要軸と同種の overflow (有限だが極大な温度値): レンジ (max − min) や
  // 上端の余白計算が Infinity になると domain が [-Infinity, NaN] 等になり、
  // 目盛りも first / last が ±Infinity の無意味な列になる。値なしと同じ
  // フォールバックへ落とす。
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) {
    return { domain: ['dataMin - 2', 'dataMax + 3'], ticks: undefined }
  }

  return { domain: [lo, hi], ticks: buildTemperatureTicks(min, max, range) }
}

/**
 * 温度軸の目盛り列 (design L4388-4392)。実データ範囲 [min, max] に収まる
 * step (5℃ / レンジ 20℃ 超で 10℃) の倍数のみを並べる。範囲が狭く 1 本も
 * 立たない場合は実データの両端を目盛りにする (両端が同値なら 1 本)。
 *
 * 目盛り本数は `MAX_TEMPERATURE_TICK_COUNT` で頭打ちにする。異常に大きい気温値が
 * 届いた場合でも反復回数がデータの大きさに比例して伸びない (= 非有界ループにならない)。
 */
function buildTemperatureTicks(
  min: number,
  max: number,
  range: number,
): number[] {
  const step = range > 20 ? 10 : 5
  const first = Math.ceil(min / step) * step
  const last = Math.floor(max / step) * step
  const count = Math.min(
    Math.round((last - first) / step) + 1,
    MAX_TEMPERATURE_TICK_COUNT,
  )
  const ticks: number[] = []
  for (let i = 0; i < count; i++) ticks.push(first + i * step)
  if (ticks.length > 0) return ticks

  const lo = Math.round(min)
  const hi = Math.round(max)
  return lo === hi ? [lo] : [lo, hi]
}

/**
 * X 軸ラベルの刻み (時間) の候補。design `forecast-today.html:5521` の
 * `[3, 6, 12]` と同じ。3 時間毎が既定で、幅が足りなければ 6 → 12 と間引く。
 */
const X_AXIS_LABEL_STEP_CANDIDATES = [3, 6, 12] as const

/**
 * X 軸ラベル 1 個ぶんに要る最小ピッチ (px)。
 *
 * ラベルを **-45° の斜め表記**にしたので (issue #6879)、隣と衝突する条件が
 * 「文字の幅」ではなく「行の高さ」になる。必要な水平送りは
 * `行高 13px ÷ sin45 ≒ 18.4px`。最小 gap 6px を足して 24px。
 *
 * 水平表示だった頃は `ラベル 32px + gap 8px = 40px` で、3 時間刻みを保てる
 * plot 幅は 360px だった。24px なら 216px まで下がり、2 カラム表示 (実測 260px)
 * でも 3 時間刻みを維持できる。design も同日 `_PITCH` を 40 → 24 にしている。
 */
const X_AXIS_LABEL_MIN_PITCH = 24

/**
 * X 軸が覆う時間数。前日 23:00 起点で翌 2:00 まで延ばした最大レンジ
 * (design `forecast-today.html:5525` の `27`)。
 * 断面によって実レンジは短くなるが、design と同じく最大値で見積もる
 * (短い側で余裕が出る方向の誤差なのでラベルが重なることはない)。
 */
const X_AXIS_SPAN_HOURS = 27

/**
 * 左右の軸・余白を除いたプロット領域の幅を求める。
 * design `forecast-today.html:5522` の `el.clientWidth - 56 - 24`
 * (左軸 56px + 右余白 24px) と同じ考え方。左軸幅は実装側が動的に決まり、
 * 右も余白だけでなく気温軸が出る場合があるので、いずれも実値を受け取る。
 */
export function computePlotWidth(
  containerWidth: number,
  leftAxisWidth: number,
  rightReservedWidth: number,
): number {
  return containerWidth - leftAxisWidth - rightReservedWidth
}

/**
 * プロット幅に応じた X 軸ラベルの刻み (時間) を決める。
 *
 * design `forecast-today.html:5521-5526` の移植。3 時間毎で始め、ラベル同士が
 * `X_AXIS_LABEL_MIN_PITCH` を割り込むなら 6 → 12 と間引く。design のコメントに
 * 「2 カラム時にプロット幅が 260px 程度まで縮む」「1440px 以下でラベル同士が
 * 重なる」と実測理由が残っている (2026-08-31 デザイナー指摘で移植。実装は
 * 3 時間固定で、狭い幅でラベルが重なっていた)。
 *
 * **幅が測れない時 (0 以下) は 3 を返す** — design も `_plotW <= 0` を
 * 「間引かない」側に倒しており (`:5525`)、初回描画やテスト環境 (レイアウト
 * エンジンを持たない happy-dom) で刻みが勝手に粗くならないようにする。
 */
export function resolveXAxisLabelStepHours(plotWidth: number): number {
  const fit = X_AXIS_LABEL_STEP_CANDIDATES.find(
    (step) =>
      plotWidth <= 0 ||
      plotWidth / (X_AXIS_SPAN_HOURS / step) >= X_AXIS_LABEL_MIN_PITCH,
  )
  // 12 時間刻みでも入らない極端な幅では、それ以上間引かず最大候補で止める。
  return (
    fit ?? X_AXIS_LABEL_STEP_CANDIDATES[X_AXIS_LABEL_STEP_CANDIDATES.length - 1]
  )
}

/**
 * 前端ラベル (前日 23:00) を出せる幅か。
 *
 * **前端は間引きの対象外なので、刻みを粗くしても隣との距離が変わらない。**
 * 範囲拡張 (AC-DFD-006a) が本体の手前に前日 23:00〜24:00 を含めるため、前端と
 * 0:00 は **1 時間 = 2 コマ** しか離れていない。通常ラベル同士 (既定 3 時間) の
 * **1/3 の間隔**しかなく、間引きは「何本目を出すか」を変えるだけなのでここには効かない。
 *
 * 斜め表記 (-45°) で必要ピッチは 24px まで下がったが、それでも
 * `plotWidth / 27 >= 24` = plot 幅 648px 以上が要る。2 カラム表示 (実測 260px) では
 * 間隔が約 9.6px しかなく、どう倒しても 0:00 と重なる。
 *
 * **重なる時は前端を落とし、0:00 を残す。** AC-DFD-006a-4 の ※ が
 * 「0:00 を必ず表示する 3 時間毎グリッドに変更する」と 0:00 を主目的に据えているため。
 * 前端の非表示は AC 本文が明示的に許していない縮退なので、spec への追記を
 * 依頼している (issue #6879)。
 *
 * 幅が測れない時 (0 以下) は**出す**側に倒す。初回描画やレイアウトエンジンを持たない
 * happy-dom で前端が勝手に消えないようにするため (`resolveXAxisLabelStepHours` と同じ方針)。
 */
export function canShowFrontEdgeLabel(plotWidth: number): boolean {
  if (plotWidth <= 0) return true
  return plotWidth / X_AXIS_SPAN_HOURS >= X_AXIS_LABEL_MIN_PITCH
}
